import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {generateRandomId, MediaDetection, MediaDetectionType, Rect} from '@legit-health/ui';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {Box, Button, Chip, Paper, Stack, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import {Vector2d} from 'konva/lib/types';
import useImage from 'use-image';
import DetectionCanvas from '@/shared/components/DetectionCanvas';
import createDetection from '@/shared/utils/diagnosticReport/createDetection';

/**
 * Detection types supported by the AIHS4 severity assessment. The endpoint
 * can only return these three kinds of lesions; the practitioner can also
 * add new ones of the same types over the image.
 */
const HS_DETECTION_TYPES: MediaDetectionType[] = [
  MediaDetectionType.Nodule,
  MediaDetectionType.Abscess,
  MediaDetectionType.DrainingTunnel
];

const DETECTION_TYPE_COLORS: Partial<Record<MediaDetectionType, string>> = {
  [MediaDetectionType.Nodule]: '#8671FF',
  [MediaDetectionType.Abscess]: 'slategrey',
  [MediaDetectionType.DrainingTunnel]: '#04AF65'
};

interface DetectionEditorProps {
  file: Blob;
  detections: MediaDetection[];
  onChange: (detections: MediaDetection[]) => void;
  t: TFunction;
}

export default function DetectionEditor({file, detections, onChange, t}: DetectionEditorProps) {
  // Creation and revocation of the object URL must live in the same effect so
  // React 18 StrictMode's mount → unmount → remount cycle never leaves
  // `useImage` loading a URL that has already been revoked. Using `useMemo`
  // + a separate cleanup effect causes a blank image in dev.
  const [imageUrl, setImageUrl] = useState<string>('');
  useEffect(
    function manageObjectUrl() {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      return function () {
        URL.revokeObjectURL(url);
      };
    },
    [file]
  );

  const [image] = useImage(imageUrl);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scaleFactor, setScaleFactor] = useState<number | null>(null);
  const [selectedDetection, setSelectedDetection] = useState<MediaDetection | null>(null);
  const [addingType, setAddingType] = useState<MediaDetectionType | null>(null);

  useLayoutEffect(
    function computeScaleFactor() {
      if (!image || !containerRef.current) {
        return;
      }
      const width = containerRef.current.clientWidth;
      if (width === 0) {
        return;
      }
      setScaleFactor(width / image.width);
    },
    [image]
  );

  const handleAddDetection = useCallback(
    function (point: Vector2d) {
      if (!image || !addingType) {
        return;
      }
      const detection = createDetection({point, detectionType: addingType, image});
      onChange([...detections, detection]);
      setAddingType(null);
    },
    [addingType, detections, image, onChange]
  );

  const handleDeleteDetection = useCallback(
    function (detection: MediaDetection) {
      onChange(detections.filter((d) => d.id !== detection.id));
      setSelectedDetection(null);
    },
    [detections, onChange]
  );

  const handleTransformDetection = useCallback(
    function (detection: MediaDetection, rect: Rect) {
      onChange(
        detections.map((d) =>
          d.id === detection.id ? {...d, rect, id: d.id ?? generateRandomId()} : d
        )
      );
    },
    [detections, onChange]
  );

  return (
    <Stack
      direction={{xs: 'column', md: 'row'}}
      spacing={2}
      alignItems={{xs: 'stretch', md: 'flex-start'}}
    >
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          cursor: addingType ? 'crosshair' : 'default'
        }}
      >
        {image && scaleFactor ? (
          <DetectionCanvas
            scaleFactor={scaleFactor}
            image={image}
            detections={detections}
            isAddingNewDetection={addingType !== null}
            onAddDetection={handleAddDetection}
            onDeleteDetection={handleDeleteDetection}
            onDragDetectionEnd={handleTransformDetection}
            onTransformDetection={handleTransformDetection}
            selectedDetection={selectedDetection}
            onSelectDetection={setSelectedDetection}
          />
        ) : (
          <Box sx={{height: 200}} />
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: {xs: 1.5, md: 2},
          width: {xs: '100%', md: 260},
          flexShrink: 0,
          alignSelf: 'stretch'
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="overline" sx={{fontWeight: 700, color: 'text.secondary'}}>
            {t('shared/imageProtocol:detections.legend')}
          </Typography>
          <Stack spacing={1}>
            {HS_DETECTION_TYPES.map((type) => {
              const count = detections.filter((d) => d.type === type).length;
              const color = DETECTION_TYPE_COLORS[type] ?? 'grey';
              const isAdding = addingType === type;
              return (
                <Stack
                  key={type}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{
                    p: {xs: 0.75, md: 1},
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isAdding ? 'primary.main' : 'divider',
                    bgcolor: isAdding ? 'action.hover' : 'transparent'
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 0.5,
                      border: `2px solid ${color}`,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{flex: 1, fontSize: {xs: '0.75rem', md: '0.875rem'}}}
                  >
                    {t(`ui:mediaDetection.${type}`)}
                  </Typography>
                  <Chip size="small" label={count} />
                  <Button
                    size="small"
                    variant={isAdding ? 'contained' : 'outlined'}
                    color={isAdding ? 'secondary' : 'primary'}
                    onClick={() => setAddingType(isAdding ? null : type)}
                    sx={{minWidth: 0, px: {xs: 1, md: 1.5}}}
                  >
                    {isAdding ? (
                      <CloseIcon fontSize="small" />
                    ) : (
                      <AddCircleOutlineIcon fontSize="small" />
                    )}
                    <Box component="span" sx={{display: {xs: 'none', md: 'inline'}, ml: 0.5}}>
                      {isAdding
                        ? t('shared/common:cancel')
                        : t('shared/imageProtocol:detections.add')}
                    </Box>
                  </Button>
                </Stack>
              );
            })}
          </Stack>
          {addingType && (
            <Typography variant="caption" color="text.secondary">
              {t('shared/imageProtocol:detections.clickImageToAdd')}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
